import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import ReactDOM from "react-dom";
import { useLocation } from "react-router-dom";
import {
  FileText, Plus, X, Loader2, Copy, Trash2, Download,
  Send, Eye, Edit3, BookOpen, Upload, Search,
  CheckCircle2, XCircle, Clock, RefreshCw, Percent,
  Building2, Hammer, FileSpreadsheet, ChevronRight, ChevronDown,
  AlertTriangle, History, ArrowRight, ListChecks,
  Ban, CalendarDays, Paperclip, FilePlus, Archive,
  RotateCcw, Users, TrendingUp, Target,
  Sparkles, BarChart3, PieChart as PieChartIcon, Wallet
} from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, Legend } from "recharts";
import axios from "axios";
import { API, useAuth } from "../../contexts/AuthContext";
import { formatPYG, formatUSD2 } from "../../utils/format";
import { Skeleton } from "../../components/ui/skeleton";
import { toast } from "sonner";
import { WORKFLOW_STEPS, STATUS_META, NEXT_STEP_LABEL, TIPO_META, UNIDADES, DEFAULT_PCT, opts, fmt } from "./constants";
import { Modal, DeadlineBadge, WorkflowStepper, daysUntil, getWfIndex } from "./SharedComponents";
import { FinancialsTab } from "./FinancialsTab";

// Fix I (2026-08-31): normalización para buscar sin distinguir mayúsculas ni
// tildes. El listado convive con "CANALIZACIÓN" y "canalizacion". El rango
// U+0300 a U+036F son los signos diacríticos que NFD separa de la letra base.
const normalizarTexto = (s) => (s == null ? "" : String(s))
  .normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

export function BudgetDetail({ budget }) {
  const pct = budget.porcentajes || DEFAULT_PCT;
  const items = budget.items || [];
  const history = budget.history || [];
  const [detailTab, setDetailTab] = useState("items");

  // Fix I (2026-08-31): buscador con salto entre coincidencias, al estilo del de
  // Google Sheets. RESALTA Y DESPLAZA, NO FILTRA: todas las filas siguen visibles
  // y en el mismo orden, para que ningún total quede sin su fila a la vista.
  const [busqueda, setBusqueda] = useState("");
  const [coincidenciaActiva, setCoincidenciaActiva] = useState(0);
  const contenedorTablaRef = useRef(null);

  const coincidencias = useMemo(() => {
    const q = normalizarTexto(busqueda).trim();
    if (!q) return [];
    const res = [];
    items.forEach((it, i) => {
      const heno = normalizarTexto(`${it.numero || ""} ${it.descripcion || ""} ${it.unidad || ""}`);
      if (heno.includes(q)) res.push(i);
    });
    return res;
  }, [items, busqueda]);

  const setCoincidencias = useMemo(() => new Set(coincidencias), [coincidencias]);

  // Al cambiar el texto se vuelve a la primera coincidencia.
  useEffect(() => { setCoincidenciaActiva(0); }, [busqueda]);

  // Desplaza el contenedor hasta dejar la coincidencia activa centrada. Se mueve
  // el scrollTop a mano en vez de usar scrollIntoView, que además del contenedor
  // arrastraría el scroll de la página entera.
  useEffect(() => {
    if (coincidencias.length === 0) return;
    const cont = contenedorTablaRef.current;
    if (!cont) return;
    const fila = cont.querySelector(`[data-fila-idx="${coincidencias[coincidenciaActiva]}"]`);
    if (!fila) return;
    cont.scrollTop = Math.max(0, fila.offsetTop - cont.clientHeight / 2);
  }, [coincidencias, coincidenciaActiva]);

  const irCoincidencia = (paso) => {
    if (coincidencias.length === 0) return;
    setCoincidenciaActiva(a => (a + paso + coincidencias.length) % coincidencias.length);
  };

  const teclaBusqueda = (e) => {
    if (e.key === "Enter") { e.preventDefault(); irCoincidencia(e.shiftKey ? -1 : 1); }
    else if (e.key === "Escape") { e.preventDefault(); setBusqueda(""); }
  };

  const idxActivo = coincidencias.length > 0 ? coincidencias[coincidenciaActiva] : -1;

  return (
    <div className="p-0">
      <div className="bg-[#2E6DA4] text-white px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-bold text-sm">SISTEMA DE GESTIÓN DE PRESUPUESTOS — CEN INGENIERIA E.A.S.</h3>
            <p className="text-xs opacity-70">N°: {budget.number} | {budget.created_at?.slice(0, 10)} | {STATUS_META[budget.status]?.label || budget.status}</p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold">{fmt(budget.total)}</p>
            <p className="text-xs opacity-70">IVA incluido</p>
            {/* Fix FG (2026-10-08): cotización en USD */}
            {budget.moneda === "USD" && budget.tipo_cambio > 0 && (
              <p className="text-sm font-semibold" data-testid="budget-detail-usd">{formatUSD2(budget.total / budget.tipo_cambio)} · 1 USD = Gs. {Math.round(budget.tipo_cambio).toLocaleString("es-PY")}</p>
            )}
          </div>
        </div>
      </div>

      {/* Workflow progress */}
      <div className="bg-[#EBF3FB] px-6 py-3 border-b border-[#C5DCF0]">
        <WorkflowStepper status={budget.status} />
        {budget.deadline && (
          <div className="flex items-center gap-2 mt-2 text-xs text-gray-600">
            <CalendarDays size={12} />
            <span>Deadline: <strong>{budget.deadline}</strong></span>
            <DeadlineBadge deadline={budget.deadline} />
          </div>
        )}
      </div>

      <div className="bg-[#EBF3FB] px-6 py-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm border-b border-[#C5DCF0]">
        <div><span className="text-xs text-gray-500">A:</span><p className="font-semibold text-gray-800">{budget.cliente || "—"}</p></div>
        <div><span className="text-xs text-gray-500">ATN:</span><p className="font-semibold text-gray-800">{budget.ingeniero_contacto || "—"}</p></div>
        <div><span className="text-xs text-gray-500">Obra:</span><p className="font-semibold text-gray-800">{budget.obra || "—"}</p></div>
        <div><span className="text-xs text-gray-500">Márgenes:</span><p className="font-semibold text-[#1A4B7A] text-xs">GM {pct.ganancia_mat_pct}% | GA {pct.ga_pct}% | GMO {pct.gmo_pct}%</p></div>
      </div>

      {/* Detail Tabs */}
      <div className="flex border-b border-gray-200 bg-white px-6">
        {[["items", "Detalle de Ítems"], ["financials", "Análisis Financiero"]].map(([t, l]) => (
          <button key={t} onClick={() => setDetailTab(t)}
            className={`px-4 py-3 text-sm font-semibold border-b-2 transition-colors ${detailTab === t ? "border-[#2E6DA4] text-[#2E6DA4]" : "border-transparent text-gray-400 hover:text-gray-600"}`}
            data-testid={`detail-tab-${t}`}>
            {l}
          </button>
        ))}
      </div>

      {detailTab === "items" && (
        <>
          {/* Fix I (2026-08-31): buscador con contador y salto. */}
          <div className="flex items-center gap-2 px-6 py-2.5 bg-white border-b border-gray-100">
            <div className="relative flex-1 max-w-sm">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={busqueda}
                onChange={e => setBusqueda(e.target.value)}
                onKeyDown={teclaBusqueda}
                placeholder="Buscar en el listado..."
                className="w-full border border-gray-300 rounded-lg pl-8 pr-3 py-1.5 text-xs focus:ring-1 focus:ring-[#2E6DA4]/30 focus:border-[#2E6DA4]"
                data-testid="detail-buscador"
              />
            </div>
            {busqueda.trim() && (
              <>
                <span className={`text-xs tabular-nums ${coincidencias.length ? "text-gray-600" : "text-red-500"}`} data-testid="detail-buscador-contador">
                  {coincidencias.length ? `${coincidenciaActiva + 1} de ${coincidencias.length}` : "sin resultados"}
                </span>
                <button type="button" onClick={() => irCoincidencia(-1)} disabled={!coincidencias.length}
                  className="p-1 rounded hover:bg-gray-100 disabled:opacity-30" title="Anterior (Shift+Enter)"
                  data-testid="detail-buscador-anterior">
                  <ChevronDown size={14} className="rotate-180 text-gray-500" />
                </button>
                <button type="button" onClick={() => irCoincidencia(1)} disabled={!coincidencias.length}
                  className="p-1 rounded hover:bg-gray-100 disabled:opacity-30" title="Siguiente (Enter)"
                  data-testid="detail-buscador-siguiente">
                  <ChevronDown size={14} className="text-gray-500" />
                </button>
                <button type="button" onClick={() => setBusqueda("")}
                  className="p-1 rounded hover:bg-gray-100" title="Limpiar (Esc)"
                  data-testid="detail-buscador-limpiar">
                  <X size={14} className="text-gray-400" />
                </button>
              </>
            )}
          </div>
          {/* Fix I (2026-08-31): el `sticky top-0` del thead ya estaba escrito pero
              no hacía nada. Medido en el navegador: `overflow-x-auto` hace que el
              navegador compute `overflow-y: auto`, así que ESTE div pasa a ser el
              contenedor de scroll del sticky — pero sin altura acotada nunca
              scrollea (scrollHeight === clientHeight) y el que se mueve es el
              <main> de la app, con lo cual el encabezado se iba de pantalla
              (top: 60px -> -190px al scrollear). Con `overflow-auto` + altura
              máxima el div scrollea de verdad y el encabezado queda fijo
              (medido: 60px -> 60px). `relative` es para que el offsetTop de cada
              fila se mida contra este div al saltar entre coincidencias. */}
          <div ref={contenedorTablaRef} className="overflow-auto max-h-[70vh] relative">
            <table className="w-full text-xs" data-testid="detail-table">
              <thead className="bg-[#2E6DA4] text-white sticky top-0 z-10">
                <tr>
                  {["N°", "DESCRIPCIÓN", "Un.", "Cant.", "C.Unit.Mat", "Mat S/IVA", "Tot.Mat", "C.Unit.MO", "MO S/IVA", "Tot.MO", "Costo Total", "Tot.GM", "Tot.GA", "Tot.GMO", "P.U.Mat", "P.U.MO", "P.UNIT", "IVA", "TOTAL FINAL"].map(h => (
                    <th key={h} className="px-2 py-2.5 text-center font-semibold whitespace-nowrap" style={{ fontSize: "0.6rem" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((item, i) => {
                  if (item.is_group) {
                    return (
                      /* Fix I (2026-08-31): data-fila-idx es el ancla del salto entre
                         coincidencias; el fondo cambia solo si la fila coincide. */
                      <tr key={i} data-fila-idx={i}
                        className={i === idxActivo ? "bg-amber-200 ring-2 ring-amber-400" : (setCoincidencias.has(i) ? "bg-emerald-100" : "bg-[#D9EAF8]")}>
                        <td className="px-3 py-2 font-bold text-[#1A4B7A]">{item.numero}</td>
                        <td className="px-3 py-2 font-bold text-[#1A4B7A] uppercase" colSpan={18}>{item.descripcion}</td>
                      </tr>
                    );
                  }
                  return (
                    /* Fix I (2026-08-31): el rayado alterno se conserva para las filas
                       que no coinciden; la coincidencia activa va en ámbar y las demás
                       en verde, como el buscador de Google Sheets. */
                    <tr key={i} data-fila-idx={i}
                      className={`border-b border-gray-50 ${i === idxActivo ? "bg-amber-200 ring-2 ring-amber-400" : (setCoincidencias.has(i) ? "bg-emerald-100" : (i % 2 === 0 ? "" : "bg-[#F8FAFB]"))}`}>
                      <td className="px-2 py-1.5 text-center text-gray-500">{item.numero}</td>
                      <td className="px-2 py-1.5 text-gray-800 font-medium">{item.descripcion}</td>
                      <td className="px-2 py-1.5 text-center text-gray-500">{item.unidad}</td>
                      <td className="px-2 py-1.5 text-right">{item.cantidad}</td>
                      <td className="px-2 py-1.5 text-right">{fmt(item.costo_unit_mat)}</td>
                      <td className="px-2 py-1.5 text-right">{fmt(item.mat_sin_iva)}</td>
                      <td className="px-2 py-1.5 text-right text-blue-600">{fmt(item.total_mat)}</td>
                      <td className="px-2 py-1.5 text-right">{fmt(item.costo_unit_mo)}</td>
                      <td className="px-2 py-1.5 text-right">{fmt(item.mo_sin_iva)}</td>
                      <td className="px-2 py-1.5 text-right text-blue-600">{fmt(item.total_mo)}</td>
                      <td className="px-2 py-1.5 text-right font-semibold">{fmt(item.costo_total)}</td>
                      <td className="px-2 py-1.5 text-right text-orange-600">{fmt(item.tot_gm)}</td>
                      <td className="px-2 py-1.5 text-right text-orange-600">{fmt(item.tot_ga)}</td>
                      <td className="px-2 py-1.5 text-right text-orange-600">{fmt(item.tot_gmo)}</td>
                      <td className="px-2 py-1.5 text-right text-[#2E6DA4] font-semibold">{fmt(item.pu_mat)}</td>
                      <td className="px-2 py-1.5 text-right text-[#2E6DA4] font-semibold">{fmt(item.pu_mo)}</td>
                      <td className="px-2 py-1.5 text-right text-[#1A4B7A] font-bold">{fmt(item.precio_unit)}</td>
                      <td className="px-2 py-1.5 text-right text-orange-500">{fmt(item.iva_item)}</td>
                      <td className="px-2 py-1.5 text-right font-bold text-[#1A4B7A]">{fmt(item.total_final)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="bg-[#1A4B7A] text-white px-6 py-4 flex items-center justify-end gap-8">
            <div className="text-right"><p className="text-xs opacity-70">SUBTOTAL NETO</p><p className="font-bold">{fmt(budget.subtotal_neto)}</p></div>
            <div className="text-right"><p className="text-xs opacity-70">IVA (10%)</p><p className="font-bold">{fmt(budget.iva_total)}</p></div>
            <div className="text-right border-l border-white/30 pl-8"><p className="text-xs opacity-70">TOTAL IVA INCLUIDO</p><p className="font-bold text-2xl">{fmt(budget.total)}</p>
              {budget.moneda === "USD" && budget.tipo_cambio > 0 && <p className="text-sm font-semibold">{formatUSD2(budget.total / budget.tipo_cambio)}</p>}</div>
          </div>

          {(budget.alcance_incluye || budget.alcance_no_incluye) && (
            <div className="p-6 border-t border-gray-100">
              <h4 className="font-bold text-[#1A4B7A] text-sm mb-3 uppercase tracking-wide">Alcance del Proyecto</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {budget.alcance_incluye && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                    <p className="text-xs font-bold text-emerald-700 mb-2">SI INCLUYE:</p>
                    <p className="text-xs text-gray-700 whitespace-pre-wrap">{budget.alcance_incluye}</p>
                  </div>
                )}
                {budget.alcance_no_incluye && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                    <p className="text-xs font-bold text-red-700 mb-2">NO INCLUYE:</p>
                    <p className="text-xs text-gray-700 whitespace-pre-wrap">{budget.alcance_no_incluye}</p>
                  </div>
                )}
              </div>
            </div>
          )}
          {budget.obs_tecnicas && (
            <div className="px-6 pb-4">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <p className="text-xs font-bold text-blue-700 mb-2">OBSERVACIONES TÉCNICAS:</p>
                <p className="text-xs text-gray-700 whitespace-pre-wrap">{budget.obs_tecnicas}</p>
              </div>
            </div>
          )}
          {(budget.marcas_cotizadas || budget.condicion_pago) && (
            <div className="px-6 pb-4 grid grid-cols-1 md:grid-cols-2 gap-3">
              {budget.marcas_cotizadas && <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3"><p className="text-xs font-bold text-yellow-700 mb-1">MARCAS COTIZADAS:</p><p className="text-xs text-gray-700">{budget.marcas_cotizadas}</p></div>}
              {budget.condicion_pago && <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3"><p className="text-xs font-bold text-yellow-700 mb-1">CONDICIÓN DE PAGO:</p><p className="text-xs text-gray-700">{budget.condicion_pago}</p></div>}
            </div>
          )}

          {/* History */}
          {history.length > 0 && (
            <div className="px-6 pb-6 border-t border-gray-100 pt-4">
              <h4 className="font-bold text-gray-700 text-sm mb-3 flex items-center gap-2"><History size={14} /> Historial de estados</h4>
              <div className="space-y-2">
                {[...history].reverse().map((h, i) => {
                  const meta = STATUS_META[h.status] || { color: "bg-gray-100 text-gray-600", icon: Clock };
                  const Icon = meta.icon;
                  return (
                    <div key={i} className="flex items-start gap-3 text-xs">
                      <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${meta.color}`}>
                        <Icon size={11} />
                      </div>
                      <div className="flex-1">
                        <span className="font-semibold text-gray-800">{h.label || h.status}</span>
                        {h.user_name && <span className="text-gray-400 ml-2">por {h.user_name}</span>}
                        {h.comment && <p className="text-gray-500 mt-0.5">{h.comment}</p>}
                      </div>
                      <span className="text-gray-400 flex-shrink-0">{h.timestamp?.slice(0, 10)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}

      {detailTab === "financials" && (
        <div className="bg-gray-50 flex-1">
          <FinancialsTab budgetId={budget._id} items={items} pct={pct} readOnly={true} budget={budget} descuento={budget?.descuento || null} />
        </div>
      )}
    </div>
  );
}

// ─── Price List Manager ────────────────────────────────────────────────────────
